import {
  AMap,
  buildSubView,
  Em,
  MapItemContent,
  modalConfirm,
  playContext,
  roomImages,
  showMsg,
  TestPage,
  TItemData,
} from "@client";
import { Style } from "@client/style";
import { NpcModel } from "@npc";
import { calcScore, NumberUtil, SceneConst, SubNamespace } from "@share";
import { Button, Input, ModalFuncProps, Radio } from "antd";
import React, { useContext, useState } from "react";

function Play({ playDone }: { playDone: () => void }) {
  const {
    params: { searchCost },
    playerState: { convenientList },
    saveResult,
  } = useContext(playContext);
  const { houses } = SceneConst.rentHouse;
  const [seenList, setSeenList] = useState(houses.map(() => false)),
    setSeen = (i: number) => {
      const _seenList = seenList.slice();
      _seenList[i] = true;
      setSeenList(_seenList);
    };
  const [tradeIndex, setTradeIndex] = useState<number>(null!);
  const items: TItemData[] = houses.map(({ name }, i) => ({
    name,
    seen: seenList[i],
  }));
  const seeItem = (i: number) => {
    if (tradeIndex != null) {
      return;
    }
    const { price, houses } = SceneConst.rentHouse,
      { name } = houses[i];
    const seeConfirm: ModalFuncProps = {
      onOk: async () => {
        setSeen(i);
        const convenient = convenientList[i];
        const range = NumberUtil.normalizeUniformDist(convenient, SceneConst.rentHouse.convenient),
          msg = {
            [NumberUtil.Range
              .I]: `符合你的住房要求，但并没有特别出众。周边环境有些不方便，有一个项目还在施工中，略显吵闹，但乐观估计总会越发展越好吧！好在房子也合适，应该可以打造成一个温馨的家！`,
            [NumberUtil.Range
              .II]: `还是有潜力的。有交通线路可以直达工作地点，周边环境已有起色，房子大小刚合适，再要求房东配上些家电就完美啦！`,
            [NumberUtil.Range.III]: `真的不错哦！交通方便，周边环境较成熟，房子安静、舒适，家电也都够用！`,
            [NumberUtil.Range
              .IV]: `非常适合你的口味！交通距离合适，周边生活方便，房间温馨明亮，家具设备齐全，邻里团结友爱，`,
          }[range],
          desc = `通过实地查看，你发现【${name}】${msg}通过综合评价，该房屋的便利指数为【${convenient}】!
            也就是说，租下该房屋，你的净效用为【${convenient - price}】。
            `;
        const confirmProps: ModalFuncProps = {
          okText: "支付",
          onOk: async () => {
            setTradeIndex(i);
            const { price, houses } = SceneConst.rentHouse,
              totalSearchCost = seenList.map((seen) => (seen ? searchCost : 0)).reduce((m, n) => m + n),
              { name } = houses[i],
              earning = convenient - price;
            await showMsg(
              <p>
                {!!totalSearchCost && (
                  <>
                    花费<Em>{totalSearchCost}</Em>的搜寻成本后，
                  </>
                )}{" "}
                你本轮租下
                <Em>{name}</Em>
                ，获得收益
                <Em>{earning}</Em>。
              </p>,
              {
                okText: "知道了",
              }
            );
            saveResult({
              score: calcScore(NumberUtil.getMin(convenientList), convenientList[i], NumberUtil.getMax(convenientList)),
              seenList,
              tradeIndex: i,
            });
            playDone();
          },
          title: name,
          maskStyle: {
            backgroundImage: `url('${roomImages[i]}')`,
            filter: "blur(.5rem)",
          },
          content: (
            <MapItemContent
              desc={desc}
              data={[
                {
                  label: "价格",
                  value: price,
                },
                {
                  label: "便利指数",
                  value: convenient,
                },
              ]}
            />
          ),
        };
        modalConfirm(confirmProps);
      },
      cancelText: "取消",
      content: searchCost ? (
        <p>
          你选择了
          <Em>{name}</Em>，是否支付
          <Em>{searchCost}</Em>元的搜寻成本查看住所信息?
        </p>
      ) : (
        <p>
          你选择了
          <Em>{name}</Em>，本次搜寻无成本。
        </p>
      ),
    };
    modalConfirm(seeConfirm);
  };
  return <AMap items={items} onClick={(i) => seeItem(i)} />;
}

function Test({ done }: { done: (answers, answerScores) => void }) {
  const radioStyle = {
    display: "block",
    height: "30px",
    lineHeight: "30px",
  };
  return (
    <TestPage
      questions={[
        {
          Content: ({ inputProps }) => (
            <div>
              <p>计算期望：如果买家预期某产品价格有一半的可能性为2000元，一半的可能性为1800元。则该买家的期望价格为</p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A. 1800</Radio>
                <Radio value={2}>B. 1900</Radio>
                <Radio value={3}>C. 2000</Radio>
                <Radio value={4}>D. 2100</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => <p>2000/2+1800/2=1900</p>,
          answer: [2],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                如果买家的净收益为产品效用减去支付价格。买家的产品净效用用货币来计量，有一半的可能性效用为3000元，一半的可能性效用为1000元。买家现在访问的卖家提供的价格均为1800元。则买家搜寻的期望净收益为：(假设买家无搜寻成本。产品不应产生负的收益，否则消费者会拒绝购买。)
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A. 600</Radio>
                <Radio value={2}>B. 1200</Radio>
                <Radio value={3}>C. 200</Radio>
                <Radio value={4}>D. 2000</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => <p>3000-1800=1200（购买）；1000-1800=-800（不购买）；期望效用为1200*0.5+0*0.5=600</p>,
          answer: [1],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                如果你在某一卖家处看到的产品的收益估计为3000元，卖家设置的销售价格为2100元。经过计算，你预期下一次搜寻的净收益为1000，搜寻需要花费的成本为200元。你是否选择搜寻？
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio value={1}>A. 继续搜寻</Radio>
                <Radio value={2}>B. 终止搜寻</Radio>
              </Radio.Group>
            </div>
          ),
          Answer: () => (
            <p>
              3000-2100=900（购买的净收益）；1000-200=800（搜寻的净收益）；当下购买的净收益&gt;继续搜寻的净收益，因此选择不搜寻。
            </p>
          ),
          answer: [2],
        },
        {
          Content: ({ inputProps }) => (
            <div>
              <p>
                如果你有50%的机会获得低报酬，50%的机会获取高报酬，且有以下6种可能供选择，你最偏好的选项为：
                <br />
                （选项. 低报酬 / 高报酬）
              </p>
              <Radio.Group {...inputProps({ margin: ".5rem" })}>
                <Radio style={radioStyle} value={1}>
                  A. 28 / 28
                </Radio>
                <Radio style={radioStyle} value={2}>
                  B. 24 / 36
                </Radio>
                <Radio style={radioStyle} value={3}>
                  C. 20 / 44
                </Radio>
                <Radio style={radioStyle} value={4}>
                  D. 16 / 52
                </Radio>
                <Radio style={radioStyle} value={5}>
                  E. 12 / 60
                </Radio>
                <Radio style={radioStyle} value={6}>
                  F. 2 / 70
                </Radio>
              </Radio.Group>
            </div>
          ),
        },
        {
          isAnalysis: true,
          Content: ({ inputProps }) => (
            <div>
              <p>请结合本次任务数据， 谈谈风险偏好、态度、心理因素是如何影响搜寻决策的？无正确的答案，只需说明理由。</p>
              <br />
              <Input.TextArea maxLength={2e3} rows={4} {...inputProps({ width: "100%" })} />
            </div>
          ),
        },
      ]}
    />
  );
}

function RentHouseIntro6th() {
  const [n, setN] = useState(0),
    [v, setV] = useState(0);
  return (
    <div>
      6. 体验随机分布：你可通过点击以下【体验搜寻】按钮，体验便利程度【U】的（随机）变化。
      <table className={Style.tableInIntro}>
        <tbody>
          <tr>
            <td rowSpan={2}>
              第<Em>{n}</Em>次搜寻
            </td>
            <td>本次搜到的房屋的便利程度值为</td>
          </tr>
          <tr>
            <td>
              <Em>{v}</Em>
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        便利程度随机、均匀地分布在
        <Em>
          {SceneConst.rentHouse.convenient.MIN}，{SceneConst.rentHouse.convenient.MAX}
        </Em>
        之间。
      </p>
      <div style={{ margin: "1rem", textAlign: "right" }}>
        <Button
          className={Style.btnPrimarySm}
          onClick={() => {
            setN(n + 1);
            setV(NumberUtil.uniformDist(SceneConst.rentHouse.convenient));
          }}
        >
          体验搜寻
        </Button>
      </div>
    </div>
  );
}

export const RentHouseView = buildSubView({
  namespace: SubNamespace.RentHouse,
  npcModel: NpcModel.RentHouse,
  label: "租房",
  withSearch: true,
  Play,
  introMsg: () => {
    const {
      price,
      houses,
      convenient: { MIN, MAX },
    } = SceneConst.rentHouse;
    return [
      ...`1.\t你必须要租一套房子，否则无法在该城市继续生存。
2.\t你的预算是【${price}】。经过初步筛查，你选出了【${houses.length}】个租金同样为【${price}】的备选房屋。
3.\t你可从这些备选房屋中，逐个搜寻让你满意的住所。你在搜寻过程中会产生成本：每次搜寻每次需花费一定的【搜寻成本】（将在每一轮解释）。
4.\t你并不清楚每一间住所的便利程度【U】：有的住所的设施和条件可能更合乎你的要求，因此节省你的成本；反之有的住所缺少必要设施、条件，则增加你的成本。
5.\t但经过了前期的调研，你对该地区的房子有了大致的了解。你知道备选房屋可以给你带来的便利程度转化成收益，最高为【${MAX}】,最低为【${MIN}】,你备选搜寻的房子的【便利程度】随机的、均匀地分布在个区间之内。`
        .trim()
        .split("\n"),
      <RentHouseIntro6th />,
      <p>
        7. 你可以通过点击【图标
        <span
          style={{
            display: "inline-block",
            width: "2.5rem",
            height: "2.5rem",
            background: "url(/MarketSearch/static/asset/img/poi-marker.png) 0 -2.5rem / 27rem no-repeat",
          }}
        />
        】 获得该住所的方便程度【U】。你需要决定是否接受住所的报价，或者继续搜寻。
      </p>,
      ...`8.\t你可以在任意时点返回之前搜索的房屋，并选择租下它。
9.你的目标是最大化（本期）租房的净收益。`
        .trim()
        .split("\n"),
    ];
  },
  npcWelcome: async () => {
    await showMsg("初来乍到，让我们先找一个地方住下吧！");
    await showMsg("你将进行两轮练习，然后会进行正式的工作搜寻。");
  },
  Test,
  initState: (s) =>
    (s.convenientList = SceneConst.rentHouse.houses.map(() => NumberUtil.uniformDist(SceneConst.rentHouse.convenient))),
});
